import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatDate, formatNumber } from '../../utils/formatters.js';

const LINE_VARIANT = { OPEN: 'neutral', PARTIAL: 'warning', COMPLETED: 'success' };

export function SchedulingAgreementDetailModal({ agreementId, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [qty, setQty] = useState({});
  const [error, setError] = useState(null);

  const detail = useQuery({ queryKey: ['inventory', 'scheduling-agreement', agreementId], queryFn: () => inventoryApi.getSchedulingAgreement(agreementId).then((r) => r.items), enabled: Boolean(agreementId) });
  const a = detail.data;
  const active = a?.status === 'ACTIVE';

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['inventory', 'scheduling-agreements'] });
    queryClient.invalidateQueries({ queryKey: ['inventory', 'scheduling-agreement', agreementId] });
  };
  const receive = useMutation({
    mutationFn: ({ lineId, quantity }) => inventoryApi.receiveScheduleLine(lineId, { quantity }),
    onSuccess: () => { setError(null); setQty({}); invalidate(); },
    onError: (e) => setError(e?.message || t('common.loadError')),
  });
  const close = useMutation({ mutationFn: () => inventoryApi.closeSchedulingAgreement(agreementId), onSuccess: invalidate, onError: (e) => setError(e?.message) });

  return (
    <Modal
      open={Boolean(agreementId)}
      onClose={onClose}
      title={a ? `${a.agreementNumber} · ${a.productSku}` : t('lp.title')}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
        {active && <Button variant="ghost" disabled={close.isPending} onClick={() => close.mutate()}>{t('lp.action.close')}</Button>}
      </>}
    >
      {a && (
        <div className="form-stack">
          <p className="muted text-sm">
            <Badge variant={active ? 'success' : 'neutral'}>{t(`lp.status.${a.status}`)}</Badge> {a.supplierName} · {a.productName}
            {a.validFrom ? ` · ${a.validFrom}-${a.validTo || ''}` : ''} · {t('lp.col.planned')}: <strong>{formatNumber(a.plannedTotal)}</strong> · {t('lp.col.received')}: <strong>{formatNumber(a.receivedTotal)}</strong>
          </p>
          <div className="settings-divider">{t('lp.field.lines')}</div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>{t('lp.line.date')}</th><th className="num">{t('lp.line.planned')}</th><th className="num">{t('lp.line.received')}</th><th>{t('col.status')}</th>{active && <th />}</tr>
              </thead>
              <tbody>
                {(a.lines || []).map((l) => (
                  <tr key={l.id}>
                    <td>{formatDate(l.deliveryDate)}</td>
                    <td className="num mono">{formatNumber(l.plannedQuantity)}</td>
                    <td className="num mono">{formatNumber(l.receivedQuantity)}</td>
                    <td><Badge variant={LINE_VARIANT[l.status]}>{t(`lp.line.${l.status}`)}</Badge></td>
                    {active && (
                      <td className="num">
                        {l.status !== 'COMPLETED' && (
                          <span style={{ display: 'inline-flex', gap: 'var(--space-1)' }}>
                            <input className="input num" style={{ width: 80 }} type="number" min="0.0001" step="any" value={qty[l.id] || ''} onChange={(e) => setQty((s) => ({ ...s, [l.id]: e.target.value }))} />
                            <Button size="sm" variant="primary" disabled={receive.isPending || !qty[l.id]} onClick={() => receive.mutate({ lineId: l.id, quantity: Number(qty[l.id]) })}>{t('lp.action.receive')}</Button>
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {error && <div className="alert danger">{error}</div>}
        </div>
      )}
    </Modal>
  );
}
